import { resolveApiBaseUrl } from "./apiBase";

const API_BASE_URL = resolveApiBaseUrl();

export type ConnectorFieldTypeOverrides = Record<string, Record<string, string>>;

export interface Connector {
  uid: string;
  key: string;
  label: string;
  type: string;
  baseUrl: string;
  config: Record<string, unknown> & {
    collections?: string[];
    fieldTypes?: ConnectorFieldTypeOverrides;
  };
  enabled: boolean;
  secretsSet: Record<string, boolean>;
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string | null;
}

export interface ConnectorCollectionField {
  name: string;
  label: string;
  inferredType: string;
  type: string;
  overridden: boolean;
}

export interface ConnectorListResponse {
  connectors: Connector[];
  supportedTypes: string[];
  encryptionConfigured: boolean;
}

export interface FileUsageReference {
  collection: string;
  uid: string;
  title: string;
  via: string;
}

export interface FileUsageFile {
  filename: string;
  name: string;
  gallery: string;
  extension: string;
  status: 'in-use' | 'orphaned';
  referencedBy: FileUsageReference[];
}

export interface FileUsageUnregistered {
  filename: string;
  extension: string;
  referencedBy: FileUsageReference[];
}

export interface FileUsageReport {
  connectorKey: string;
  inventoryCollection: string;
  scanCollections: string[];
  files: FileUsageFile[];
  unregistered: FileUsageUnregistered[];
  scanErrors: { collection: string; error: string }[];
  summary: {
    inventoryTotal: number;
    inUse: number;
    orphaned: number;
    unregisteredReferences: number;
    byExtension: Record<string, number>;
  };
}

function authHeaders(json = true): Record<string, string> {
  const token = localStorage.getItem('authToken') || localStorage.getItem('idToken');
  const headers: Record<string, string> = {};
  if (json) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE_URL}/connectors${path}`, {
    ...init,
    headers: { ...authHeaders(), ...(init?.headers || {}) },
    credentials: 'include',
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.success === false) {
    const message =
      data?.error ||
      data?.message ||
      `Request failed (${response.status})`;
    throw new Error(Array.isArray(data?.errors) ? `${message}: ${data.errors.join(', ')}` : message);
  }
  return data as T;
}

export const connectorsAPI = {
  list: () => request<ConnectorListResponse>(''),

  get: (key: string) => request<{ connector: Connector }>(`/${encodeURIComponent(key)}`),

  create: (input: {
    key: string;
    label?: string;
    type?: string;
    baseUrl: string;
    config?: Record<string, unknown>;
    enabled?: boolean;
    dtoken?: string;
  }) => request<{ connector: Connector }>('', { method: 'POST', body: JSON.stringify(input) }),

  update: (
    key: string,
    patch: Partial<Pick<Connector, 'label' | 'type' | 'baseUrl' | 'config' | 'enabled'>>
  ) =>
    request<{ connector: Connector }>(`/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  /** Set or rotate a write-only secret. Empty value clears it. */
  setSecret: (key: string, value: string, name = 'dtoken') =>
    request<{ connector: Connector }>(`/${encodeURIComponent(key)}/secrets`, {
      method: 'POST',
      body: JSON.stringify({ name, value }),
    }),

  remove: (key: string) =>
    request<{ success: boolean }>(`/${encodeURIComponent(key)}`, { method: 'DELETE' }),

  test: (key: string) =>
    request<{ ok: boolean; error?: string }>(`/${encodeURIComponent(key)}/test`, { method: 'POST' }),

  /** Read-only report: which remote uploaded files are in use vs orphaned. */
  fileUsage: (key: string) =>
    request<{ report: FileUsageReport }>(`/${encodeURIComponent(key)}/file-usage`),

  /** List images from the connector's remote upload registry. Accessible to any user with connector ACL access. */
  images: (key: string) =>
    request<{ images: Array<{ filename: string; name: string; gallery: string | null; modified: string | null }> }>(
      `/${encodeURIComponent(key)}/images`
    ),

  remoteCollections: (key: string) =>
    request<{ collections: string[] }>(`/${encodeURIComponent(key)}/remote-collections`),

  collectionFields: (key: string, remoteCollection: string) =>
    request<{
      remoteCollection: string;
      fields: ConnectorCollectionField[];
      overrides: Record<string, string>;
    }>(
      `/${encodeURIComponent(key)}/collections/${encodeURIComponent(remoteCollection)}/fields`
    ),

  importCollection: (
    key: string,
    input: { remoteCollection: string; name: string; slug?: string; organization?: string; menuFolder?: string; sampleSize?: number }
  ) =>
    request<{ component: unknown; inferredFields: number }>(`/${encodeURIComponent(key)}/import`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};
